import { IMPROVEMENTS_ANCHORS, IMPROVEMENTS_CONTEXTS } from './anchors'
import { SHARED_ANCHORS } from '../shared/anchors'
import { withScopes } from '../shared/scopes'
import { earsSteps, taskCardSteps } from '../shared/steps'
import type { IOnboardingScenario } from '../../types/IOnboardingScenario'
import type { IOnboardingScope } from '../../types/IOnboardingScope'
import type { IOnboardingStep } from '../../types/IOnboardingStep'

const { feed, create, settings, registry, rating, form } = IMPROVEMENTS_ANCHORS
const ctx = IMPROVEMENTS_CONTEXTS
const MANAGE = 'improvements.canManage'

const settingsExpertsStep: IOnboardingStep = { id: 'improvements.settings.expertsTab', anchors: [settings.expertsPanel], context: ctx.settings, condition: MANAGE }
const settingsTypesStep: IOnboardingStep = { id: 'improvements.settings.typesTab', anchors: [settings.typesTab], context: ctx.settings, condition: MANAGE }

/* Шаг о видах ППУ открывает их вкладку: иначе под текстом о председателях остаётся список экспертов */
const typesTabScope: IOnboardingScope = {
  open: { context: ctx.settings, action: 'openTypesTab' },
  close: { context: ctx.settings, action: 'openExpertsTab' },
}

const feedSteps: IOnboardingStep[] = [
  { id: 'improvements.intro', anchors: [], context: ctx.feed },
  { id: 'improvements.create', anchors: [feed.create], context: ctx.feed },
  ...withScopes(['createModal'], [
    { id: 'improvements.create.type', anchors: [create.type], context: ctx.feed },
    { id: 'improvements.create.anonymous', anchors: [create.anonymous], context: ctx.feed },
  ]),
  ...withScopes(['actionsMenu'], [
    { id: 'improvements.settings', anchors: [feed.settings], context: ctx.feed, condition: MANAGE },
  ]),
  { id: 'improvements.navSettings', anchors: [IMPROVEMENTS_ANCHORS.nav.settings], context: ctx.feed, condition: MANAGE, placement: 'right' },
  ...withScopes(['settingsPage'], [settingsExpertsStep]),
  ...withScopes(['settingsPage', 'typesTab'], [settingsTypesStep]),
  ...withScopes(['actionsMenu'], [
    { id: 'improvements.registry', anchors: [feed.registry], context: ctx.feed, condition: MANAGE },
    { id: 'improvements.rating', anchors: [feed.rating], context: ctx.feed, condition: MANAGE },
  ]),
  { id: 'improvements.requiresAction', anchors: [feed.requiresAction], context: ctx.feed },
  { id: 'toolbar.reports', anchors: [SHARED_ANCHORS.toolbar.reports], context: ctx.feed },
  { id: 'improvements.filter', anchors: [feed.filter], context: ctx.feed },
  { id: 'toolbar.onboarding', anchors: [SHARED_ANCHORS.toolbar.onboarding], context: ctx.feed },
  { id: 'improvements.demoCard', anchors: [feed.demoCard], context: ctx.feed },
]

const registrySteps: IOnboardingStep[] = [
  { id: 'improvements.registry.intro', anchors: [], context: ctx.registry },
  { id: 'improvements.registry.search', anchors: [registry.search], context: ctx.registry },
  { id: 'improvements.registry.requiresAction', anchors: [registry.requiresAction], context: ctx.registry },
  { id: 'toolbar.reports', anchors: [SHARED_ANCHORS.toolbar.reports], context: ctx.registry },
  { id: 'improvements.registry.filters', anchors: [registry.filterFirst, registry.filterLast], context: ctx.registry },
  { id: 'improvements.registry.resetFilters', anchors: [registry.resetFilters], context: ctx.registry },
  { id: 'improvements.registry.demoRow', anchors: [registry.demoRow], context: ctx.registry },
]

const formSteps: IOnboardingStep[] = [
  ...withScopes(['form'], [
    { id: 'improvements.form.type', anchors: [form.type], context: ctx.form },
    { id: 'improvements.form.decision', anchors: [form.decision], context: ctx.form },
    { id: 'improvements.form.reviewTab', anchors: [form.reviewTab], context: ctx.form },
  ]),
  ...withScopes(['form', 'reviewTab'], [
    { id: 'improvements.form.chair', anchors: [form.chair], context: ctx.form },
    { id: 'improvements.form.assignExperts', anchors: [form.assignExperts], context: ctx.form },
    { id: 'improvements.form.experts', anchors: [form.experts], context: ctx.form, placement: 'top' },
  ]),
  ...withScopes(['form'], [
    { id: 'improvements.form.actions', anchors: [form.actions], context: ctx.form, placement: 'top' },
    { id: 'improvements.form.vote', anchors: [form.vote], context: ctx.form, placement: 'top' },
    { id: 'improvements.form.historyTab', anchors: [form.historyTab], context: ctx.form },
    { id: 'improvements.form.tasksEar', anchors: [SHARED_ANCHORS.ear.tasks], context: ctx.form, placement: 'left' },
  ]),
  ...withScopes(['form', 'tasksPane'], taskCardSteps(ctx.formTasks)),
  ...earsSteps(ctx.form, ['form']),
]

/* Области формы общие для всех сценариев ППУ, отличается только то, как открывается сама форма */
const formInnerScopes: Record<string, IOnboardingScope> = {
  reviewTab: {
    open: { context: ctx.form, action: 'openReviewTab' },
    close: { context: ctx.form, action: 'closeReviewTab' },
  },
  tasksPane: {
    open: { context: ctx.formTasks, action: 'openTasksPane' },
    close: { context: ctx.formTasks, action: 'closeTasksPane' },
  },
  chatPane: {
    open: { context: ctx.form, action: 'openDemoChat' },
    close: { context: ctx.form, action: 'closeDemoChat' },
  },
}

export const improvementsScenarios: IOnboardingScenario[] = [
  {
    id: 'improvements',
    section: 'improvement',
    isMain: true,
    scopes: {
      actionsMenu: { open: { context: ctx.feed, action: 'openActionsMenu' } },
      createModal: {
        open: { context: ctx.feed, action: 'openCreateModal' },
        close: { context: ctx.feed, action: 'closeCreateModal' },
      },
      settingsPage: {
        open: { context: ctx.feed, action: 'openSettings' },
        close: { context: ctx.settings, action: 'backToFeed' },
      },
      typesTab: typesTabScope,
      form: {
        open: { context: ctx.feed, action: 'openDemoForm' },
        close: { context: ctx.feed, action: 'closeForm' },
      },
      ...formInnerScopes,
    },
    steps: [...feedSteps, ...formSteps],
  },
  {
    id: 'improvements.registry',
    section: 'improvement',
    isMain: false,
    scopes: {
      form: {
        open: { context: ctx.registry, action: 'openDemoForm' },
        close: { context: ctx.registry, action: 'closeForm' },
      },
      ...formInnerScopes,
    },
    steps: [...registrySteps, ...formSteps],
  },
  {
    id: 'improvements.form',
    section: 'improvement',
    isMain: false,
    scopes: {
      form: {
        open: { context: ctx.form, action: 'showDemo' },
        close: { context: ctx.form, action: 'showReal' },
      },
      ...formInnerScopes,
    },
    steps: formSteps,
  },
  {
    id: 'improvements.settings',
    section: 'improvement',
    isMain: false,
    scopes: {
      typesTab: typesTabScope,
    },
    steps: [
      { id: 'improvements.settings.intro', anchors: [], context: ctx.settings },
      settingsExpertsStep,
      ...withScopes(['typesTab'], [settingsTypesStep]),
      { id: 'improvements.settings.createType', anchors: [settings.createType], context: ctx.settings, scopes: ['typesTab'] },
    ],
  },
  {
    id: 'improvements.rating',
    section: 'improvement',
    isMain: false,
    scopes: {},
    steps: [
      { id: 'improvements.rating.table', anchors: [rating.table], context: ctx.rating },
    ],
  },
]
